import * as React from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import { MailCheck } from "lucide-react"
import { toast } from "sonner"

import { AuthLinks, AuthPanel } from "@/components/auth-panel"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { requestPasswordResetFn } from "@/lib/app.functions"

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [{ title: "Demo password reset · Harbor & Pine Realty" }],
  }),
  component: ForgotPasswordPage,
})

function ForgotPasswordPage() {
  const requestReset = useServerFn(requestPasswordResetFn)
  const [message, setMessage] = React.useState("")
  const [pending, setPending] = React.useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPending(true)
    const form = new FormData(event.currentTarget)
    try {
      const result = await requestReset({
        data: { email: String(form.get("email") ?? "") },
      })
      setMessage(result.message)
      toast.success("Reset request recorded.")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to request.")
    } finally {
      setPending(false)
    }
  }

  return (
    <AuthPanel
      eyebrow="Account recovery"
      title="Reset password"
      intro="This demonstrates a neutral, enumeration-safe reset response. The public demo account passwords never change through this form."
    >
      {message ? (
        <div className="border border-primary bg-secondary p-4 text-sm leading-6">
          <MailCheck className="mb-3 size-5 text-primary" />
          {message}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="reset-email" className="field-label">
              Account email
            </Label>
            <Input
              id="reset-email"
              name="email"
              type="email"
              required
              className="field-input"
            />
          </div>
          <Button
            type="submit"
            className="field-button w-full"
            size="lg"
            disabled={pending}
          >
            <MailCheck />
            {pending ? "Requesting…" : "Request reset"}
          </Button>
        </form>
      )}
      <AuthLinks mode="reset" />
    </AuthPanel>
  )
}
